// Debug sidebar shell for Phoebe chat: run stats panel and the three-tab
// sidebar. The snapshot model lives in phoebe_chat_debug_snapshot.ts and the
// event timeline view in phoebe_chat_debug_event_timeline.tsx.
import { Activity, Bug, Gauge, Link } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { PhoebeChatDebugEventTimeline } from '@apps/web/components/phoebe_chat_debug_event_timeline';
import { usePhoebeChatDebugTimelineFocus } from '@apps/web/components/phoebe_chat_debug_focus';
import {
    type PhoebeChatDebugLinkedResource,
    type PhoebeChatDebugSnapshot,
    shortId,
} from '@apps/web/components/phoebe_chat_debug_snapshot';
import { DynamicWidgetDataInspector } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_data_inspector';
import { useDynamicWidgetInspectorState } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_inspector_store';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import { ScrollArea } from '@apps/web/components/ui/scroll_area';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '@apps/web/components/ui/tabs';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import { cn } from '@apps/web/lib/utils';

export function PhoebeChatDebugPanelToggle({
    isOpen,
    onToggle,
}: {
    readonly isOpen: boolean;
    readonly onToggle: () => void;
}) {
    const label = isOpen ? 'Hide debug' : 'Show debug';

    return (
        <Tooltip delayDuration={300}>
            <TooltipTrigger asChild={true}>
                <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={onToggle}
                    aria-label={label}
                    aria-pressed={isOpen}
                    className="h-8 w-8"
                >
                    <Bug className="h-4 w-4" />
                </Button>
            </TooltipTrigger>
            <TooltipContent>
                <p>{label}</p>
            </TooltipContent>
        </Tooltip>
    );
}

function formatNumber(value: number | null): string {
    return value === null ? 'n/a' : value.toLocaleString();
}

function formatPercent(value: number | null): string {
    return value === null ? 'n/a' : `${value.toFixed(1)}%`;
}

function formatResourceCount(count: number): string {
    return count === 1 ? '1 resource' : `${count} resources`;
}

function DebugMetric({
    label,
    value,
    tone = 'default',
}: {
    readonly label: string;
    readonly value: string;
    readonly tone?: 'default' | 'muted';
}) {
    return (
        <div className="border-border/70 min-w-0 border-b py-2">
            <div className="text-muted-foreground text-[11px] font-medium">
                {label}
            </div>
            <div
                className={cn(
                    'truncate font-mono text-xs',
                    tone === 'muted' && 'text-muted-foreground',
                )}
            >
                {value}
            </div>
        </div>
    );
}

function LinkedResourceRow({
    resource,
}: {
    readonly resource: PhoebeChatDebugLinkedResource;
}) {
    return (
        <div className="border-border/70 border-b py-2">
            <div className="flex min-w-0 items-center gap-2">
                <Badge
                    variant="secondary"
                    className="h-5 rounded-sm px-1.5 font-mono text-[10px]"
                >
                    {resource.resource_type}
                </Badge>
                <span className="text-muted-foreground font-mono text-[10px]">
                    {resource.role}
                </span>
            </div>
            <div
                className="mt-1 truncate font-mono text-xs"
                title={resource.resource_id}
            >
                {resource.resource_id}
            </div>
            {resource.created_by_event_id ? (
                <div className="text-muted-foreground mt-0.5 font-mono text-[10px]">
                    event {shortId(resource.created_by_event_id)}
                </div>
            ) : null}
        </div>
    );
}

export function PhoebeChatDebugRunStats({
    snapshot,
}: {
    readonly snapshot: PhoebeChatDebugSnapshot | null;
}) {
    return (
        <ScrollArea className="h-full">
            <div className="px-3 pb-4">
                <section className="py-2">
                    <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold">
                        <Activity className="h-3.5 w-3.5" />
                        Run
                    </div>
                    <DebugMetric
                        label="Conversation"
                        value={shortId(snapshot?.conversationId ?? null)}
                    />
                    <DebugMetric
                        label="Phoebe run"
                        value={shortId(snapshot?.phoebeRunId ?? null)}
                    />
                    <DebugMetric
                        label="Agent generation"
                        value={
                            snapshot
                                ? (snapshot.agentGeneration ?? 'null')
                                : 'n/a'
                        }
                    />
                    <DebugMetric
                        label="Connection"
                        value={snapshot?.connectionStatus ?? 'idle'}
                    />
                    <DebugMetric
                        label="Event read"
                        value={
                            snapshot
                                ? snapshot.eventReadEnabled
                                    ? 'enabled'
                                    : 'disabled'
                                : 'n/a'
                        }
                    />
                    <DebugMetric
                        label="Run status"
                        value={snapshot?.runState?.status ?? 'n/a'}
                    />
                    <DebugMetric
                        label="Activity type"
                        value={snapshot?.runState?.activity_type ?? 'n/a'}
                    />
                </section>
                <section className="py-2">
                    <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold">
                        <Link className="h-3.5 w-3.5" />
                        Linked Resources
                    </div>
                    <div className="text-muted-foreground mb-1 font-mono text-[11px]">
                        {formatResourceCount(
                            snapshot?.linkedResources.length ?? 0,
                        )}
                    </div>
                    {snapshot?.linkedResources.length ? (
                        snapshot.linkedResources.map((resource) => (
                            <LinkedResourceRow
                                key={`${resource.resource_type}-${resource.resource_id}-${resource.role}`}
                                resource={resource}
                            />
                        ))
                    ) : (
                        <div className="border-border/70 text-muted-foreground border-b py-3 text-xs">
                            No linked resources.
                        </div>
                    )}
                </section>
                <section className="py-2">
                    <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold">
                        <Gauge className="h-3.5 w-3.5" />
                        Tokens
                    </div>
                    <DebugMetric
                        label="Model"
                        value={snapshot?.tokenUsage.model ?? 'n/a'}
                    />
                    <DebugMetric
                        label="Reported input"
                        value={formatNumber(
                            snapshot?.tokenUsage.reportedInputTokens ?? null,
                        )}
                    />
                    <DebugMetric
                        label="Reported output"
                        value={formatNumber(
                            snapshot?.tokenUsage.reportedOutputTokens ?? null,
                        )}
                    />
                    <DebugMetric
                        label="Reported total"
                        value={formatNumber(
                            snapshot?.tokenUsage.reportedTotalTokens ?? null,
                        )}
                    />
                    <DebugMetric
                        label="Payload estimate"
                        value={formatNumber(
                            snapshot?.tokenUsage.estimatedPayloadTokens ?? null,
                        )}
                    />
                    <DebugMetric
                        label="Context input"
                        value={formatNumber(
                            snapshot?.tokenUsage.contextInputTokens ?? null,
                        )}
                    />
                    <DebugMetric
                        label="Context window"
                        value={formatNumber(
                            snapshot?.tokenUsage.contextWindowTokens ?? null,
                        )}
                    />
                    <DebugMetric
                        label="Context usage"
                        value={formatPercent(
                            snapshot?.tokenUsage.contextUsagePercent ?? null,
                        )}
                    />
                </section>
            </div>
        </ScrollArea>
    );
}

export const PHOEBE_CHAT_DEBUG_TAB_VALUES = [
    'event-timeline',
    'run-stats',
    'widgets',
] as const;

export type PhoebeChatDebugTab = (typeof PHOEBE_CHAT_DEBUG_TAB_VALUES)[number];

export const DEFAULT_PHOEBE_CHAT_DEBUG_TAB: PhoebeChatDebugTab =
    'event-timeline';

function isPhoebeChatDebugTab(value: string): value is PhoebeChatDebugTab {
    return (PHOEBE_CHAT_DEBUG_TAB_VALUES as ReadonlyArray<string>).includes(
        value,
    );
}

export function PhoebeChatDebugSidebar({
    snapshot,
    tab: tabProp,
    onTabChange,
}: {
    readonly snapshot: PhoebeChatDebugSnapshot | null;
    readonly tab?: PhoebeChatDebugTab;
    readonly onTabChange?: (tab: PhoebeChatDebugTab) => void;
}) {
    const [uncontrolledTab, setUncontrolledTab] = useState<PhoebeChatDebugTab>(
        DEFAULT_PHOEBE_CHAT_DEBUG_TAB,
    );
    const isTabControlled = tabProp !== undefined;
    const tab = tabProp ?? uncontrolledTab;
    const debugTimelineFocus = usePhoebeChatDebugTimelineFocus();
    const tabRef = useRef(tab);
    tabRef.current = tab;
    const setTab = useCallback(
        (next: string) => {
            if (!isPhoebeChatDebugTab(next) || next === tabRef.current) {
                return;
            }
            if (!isTabControlled) {
                setUncontrolledTab(next);
            }
            onTabChange?.(next);
        },
        [isTabControlled, onTabChange],
    );
    const { selection } = useDynamicWidgetInspectorState();
    const selectedBindingId = selection?.bindingId ?? null;

    // Picking a bound value in a rendered card, or using "Show source" on a
    // widget error, must land on the inspector whichever tab was open.
    useEffect(() => {
        if (selectedBindingId !== null) {
            setTab('widgets');
        }
    }, [selectedBindingId, setTab]);

    useEffect(() => {
        if (debugTimelineFocus.eventId !== null) {
            setTab('event-timeline');
        }
    }, [debugTimelineFocus.eventId, debugTimelineFocus.requestId, setTab]);

    return (
        <aside className="bg-background text-foreground flex h-full min-h-0 flex-col border-l">
            <div className="border-b px-3 py-3">
                <h2 className="text-sm font-semibold">Debug</h2>
            </div>
            <Tabs
                value={tab}
                onValueChange={setTab}
                className="min-h-0 flex-1 gap-0"
            >
                <div className="border-b px-3 py-2">
                    <TabsList className="grid h-8 w-full grid-cols-3 rounded-md">
                        <TabsTrigger
                            className="text-xs"
                            value="event-timeline"
                            aria-label="Event Timeline"
                        >
                            Timeline
                        </TabsTrigger>
                        <TabsTrigger
                            className="text-xs"
                            value="run-stats"
                            aria-label="Run Stats"
                        >
                            Stats
                        </TabsTrigger>
                        <TabsTrigger className="text-xs" value="widgets">
                            Widgets
                        </TabsTrigger>
                    </TabsList>
                </div>
                <TabsContent
                    value="run-stats"
                    className="mt-0 min-h-0 overflow-hidden"
                >
                    <PhoebeChatDebugRunStats snapshot={snapshot} />
                </TabsContent>
                <TabsContent
                    value="event-timeline"
                    className="mt-0 min-h-0 overflow-hidden"
                >
                    <PhoebeChatDebugEventTimeline snapshot={snapshot} />
                </TabsContent>
                <TabsContent
                    value="widgets"
                    className="mt-0 min-h-0 overflow-hidden"
                >
                    <DynamicWidgetDataInspector />
                </TabsContent>
            </Tabs>
        </aside>
    );
}
